import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import {
  Mail, Loader2, GraduationCap, Send, Clock, Calendar, MessageCircle
} from 'lucide-react';
import { createMessageRequestId } from '../../lib/manualMessaging';

const API = API_BASE;

// Standard training types — ids MUST match canonical training requirement_ids
// (see MANDATORY_TRAINING_HCA in backend/unified_compliance_engine.py and
// TRAINING_ALIASES in backend/services/training_evaluator.py). These ids are
// persisted in email_requests.context.training_types and echoed to the worker
// upload page, so they are NOT pure display — they must stay canonical.
const TRAINING_TYPES = [
  { id: 'safeguarding', label: 'Safeguarding (Adults or Children — admin review required)' },
  { id: 'manual_handling', label: 'Moving & Handling' },
  { id: 'fire_safety', label: 'Fire Safety' },
  { id: 'health_safety', label: 'Health & Safety' },
  { id: 'basic_life_support', label: 'Basic Life Support' },
  { id: 'infection_control', label: 'Infection Prevention & Control' },
  { id: 'information_governance', label: 'Information Governance / GDPR' },
  { id: 'prevent', label: 'Prevent' },
];

/**
 * TrainingRequestDialog - Send email request for training certificates to employee
 * 
 * Features:
 * - Select specific training types to request
 * - Include renewals option
 * - Custom message
 * - Due date setting
 */
export default function TrainingRequestDialog({ 
  employeeId, 
  employeeName,
  employeeEmail,
  open,
  initialSelectedTypes = [],
  initialIncludeRenewals = false,
  onClose,
  onComplete
}) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [includeRenewals, setIncludeRenewals] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [dueDays, setDueDays] = useState(14);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRequests, setExistingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loadingWhatsappStatus, setLoadingWhatsappStatus] = useState(false);
  const [whatsappRequestId, setWhatsappRequestId] = useState('');
  
  const { token } = useAuth();

  useEffect(() => {
    if (!open) return;

    const allowedTypeIds = new Set(TRAINING_TYPES.map((type) => type.id));
    const safeDefaults = [...new Set(
      (Array.isArray(initialSelectedTypes) ? initialSelectedTypes : [])
        .filter((typeId) => allowedTypeIds.has(typeId))
    )];

    setSelectedTypes(safeDefaults);
    setIncludeRenewals(Boolean(initialIncludeRenewals));
  }, [open, initialSelectedTypes, initialIncludeRenewals]);

  // Fetch existing requests
  useEffect(() => {
    if (open && employeeId) {
      fetchExistingRequests();
      fetchWhatsappStatus();
    }
  }, [open, employeeId, token]);

  const fetchExistingRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/training/requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchWhatsappStatus = async () => {
    setLoadingWhatsappStatus(true);
    try {
      const response = await axios.get(
        `${API}/staff-communications/whatsapp/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWhatsappStatus(response.data || null);
    } catch (err) {
      console.error('Failed to fetch WhatsApp status:', err);
      setWhatsappStatus(null);
    } finally {
      setLoadingWhatsappStatus(false);
    }
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const selectAll = () => {
    setSelectedTypes(TRAINING_TYPES.map(t => t.id));
  };

  const clearAll = () => {
    setSelectedTypes([]);
  };

  const handleSubmit = async () => {
    if (selectedTypes.length === 0 && !includeRenewals) {
      toast.error('Please select at least one training type or include renewals');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/training/request-certificates`,
        {
          training_types: selectedTypes.length > 0 ? selectedTypes : null,
          include_renewals: includeRenewals,
          custom_message: customMessage.trim() || null,
          due_days: dueDays
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'duplicate_blocked') {
        toast.info('An active training certificate request already exists for this worker');
      } else if (['sent', 'resent', 'created'].includes(response.data.status)) {
        toast.success(`Training certificate request sent to ${employeeEmail || 'the worker'}`);
      } else if (response.data.status === 'send_failed') {
        toast.warning('Request was created but the email could not be sent. Check the worker email address.');
      } else if (response.data.status === 'already_satisfied') {
        toast.info('The requested training requirement is already satisfied');
      }

      if (onComplete) onComplete(response.data);
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppSubmit = async () => {
    if (selectedTypes.length === 0 && !includeRenewals) {
      toast.error('Please select at least one training type or include renewals');
      return;
    }

    const nextRequestId = whatsappRequestId || createMessageRequestId();
    if (!whatsappRequestId) setWhatsappRequestId(nextRequestId);

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/staff-communications/send-whatsapp`,
        {
          template_key: 'staff_training_reminder',
          request_id: nextRequestId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWhatsappRequestId('');
      toast.success(
        response.data?.idempotent_replay
          ? 'WhatsApp training reminder already sent'
          : `WhatsApp training reminder sent to ${employeeName || 'the worker'}`
      );

      if (onComplete) {
        onComplete({
          ...(response.data || {}),
          delivery_channel: 'whatsapp',
          training_types: selectedTypes,
          include_renewals: includeRenewals
        });
      }
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send WhatsApp training reminder');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTypes([]);
    setIncludeRenewals(false);
    setCustomMessage('');
    setDueDays(14);
    setWhatsappRequestId('');
    if (onClose) onClose();
  };

  const whatsappTrainingReady = Boolean(
    whatsappStatus?.provider?.configured &&
    (whatsappStatus?.templates?.configured_template_keys || []).includes('staff_training_reminder')
  );

  // Count pending requests
  const pendingCount = existingRequests.filter((request) =>
    ['pending_send', 'sent', 'opened', 'clicked', 'action_started', 'pending'].includes(
      String(request?.status || '').toLowerCase()
    )
  ).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Request Training Certificates
          </DialogTitle>
          <DialogDescription>
            Send an email to {employeeName || 'the employee'} requesting training certificates.
            {employeeEmail && (
              <span className="block text-xs mt-1">
                Will be sent to: <span className="font-medium">{employeeEmail}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Pending Requests Warning */}
          {pendingCount > 0 && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <Clock className="h-4 w-4" />
                <span>{pendingCount} pending request(s) already sent</span>
              </div>
            </div>
          )}

          {/* Training Types Selection */}
          <div className="space-y-2">
            {(initialSelectedTypes?.length > 0 || initialIncludeRenewals) && (
              <p className="text-xs text-text-muted" data-testid="training-request-prefill-note">
                CareOps preselected mandatory training that is expired or due for renewal. Review the request before sending.
              </p>
            )}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Training Certificates Needed</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={selectAll}
                  className="text-xs h-7"
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="text-xs h-7"
                >
                  Clear
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-2 border rounded-lg bg-[#F8FAFA]">
              {TRAINING_TYPES.map(type => (
                <div 
                  key={type.id}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedTypes.includes(type.id) 
                      ? 'bg-primary/10 border border-primary/20' 
                      : 'hover:bg-white'
                  }`}
                  onClick={() => toggleType(type.id)}
                >
                  <Checkbox 
                    checked={selectedTypes.includes(type.id)}
                    onCheckedChange={() => toggleType(type.id)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs">{type.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Include Renewals */}
          <div className="flex items-center gap-3 p-3 bg-[#F8FAFA] rounded-lg border">
            <Checkbox 
              id="include-renewals"
              checked={includeRenewals}
              onCheckedChange={setIncludeRenewals}
            />
            <div>
              <Label htmlFor="include-renewals" className="text-sm font-medium cursor-pointer">
                Include expiring/expired training renewals
              </Label>
              <p className="text-xs text-text-muted">
                Automatically request certificates for any training due for renewal
              </p>
            </div>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Response Due In
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={90}
                value={dueDays}
                onChange={(e) => setDueDays(parseInt(e.target.value) || 14)}
                className="w-20 rounded-lg"
              />
              <span className="text-sm text-text-muted">days</span>
            </div>
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Custom Message (Optional)</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add any specific instructions or notes for the employee..."
              className="rounded-lg min-h-[80px]"
              maxLength={500}
            />
            <p className="text-xs text-text-muted text-right">
              {customMessage.length}/500
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <p className="font-medium">WhatsApp option</p>
          <p className="mt-1">
            WhatsApp sends Osabea&apos;s approved training reminder with a fresh secure CareOps link.
            The worker sees the current missing, expired and renewal actions in their Training area.
            It does not approve or change any training record.
          </p>
          {!loadingWhatsappStatus && !whatsappTrainingReady ? (
            <p className="mt-1 text-amber-800">
              WhatsApp training reminders are not currently available for this workspace. Email remains available.
            </p>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:flex-wrap">
          <Button variant="outline" onClick={handleClose} className="rounded-xl">
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleWhatsAppSubmit}
            disabled={
              isSubmitting ||
              loadingWhatsappStatus ||
              !whatsappTrainingReady ||
              (selectedTypes.length === 0 && !includeRenewals)
            }
            className="rounded-xl"
            data-testid="send-training-whatsapp-btn"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MessageCircle className="h-4 w-4 mr-2" />
            )}
            Send WhatsApp Reminder
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (selectedTypes.length === 0 && !includeRenewals)}
            className="bg-primary hover:bg-primary-hover text-white rounded-xl"
            data-testid="send-training-request-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

